import { useEffect, useState } from 'react';

/**
 * 后备闸门。`requestAnimationFrame` 在后台 / 被遮挡的 renderer 里会被
 * Chromium 完全暂停(Electron 的 `backgroundThrottling` 默认就是开的),所以
 * 光靠一次 rAF 排程的任务在那种标签页里可能永远不开始。前台时 rAF 约 16ms
 * 就到,跑在这个闸门前面,排程语义不变;被暂停时由它兜底。
 */
const FRAME_FALLBACK_MS = 100;

/**
 * Run `task` after the current frame. Returns a cancel function.
 *
 * Used to keep create / first paint off `openDoc`, `blockSuiteDoc.load`,
 * `createEditor`, and full-library sidebar subscriptions.
 *
 * 让出一帧是为了首屏,不是为了「等绘制」:必要的加载不能因为这个窗口没在绘制
 * 就停住(后台标签页里打开文档就是这种情况)。所以 rAF 和一个短定时器赛跑,
 * 谁先到谁执行,另一个当场取消。
 */
export function afterCurrentFrame(task: () => void): () => void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(frame);
    clearTimeout(timer);
    task();
  };
  const frame = requestAnimationFrame(run);
  const timer = setTimeout(run, FRAME_FALLBACK_MS);
  return () => {
    done = true;
    cancelAnimationFrame(frame);
    clearTimeout(timer);
  };
}

/** False on the first paint; true after the current frame. */
export function useReadyAfterCurrentFrame(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => afterCurrentFrame(() => setReady(true)), []);
  return ready;
}
